package com.speedsoftware.nibbler.ui.auth

import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.layout.widthIn
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.Button
import androidx.compose.material3.ButtonDefaults
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.OutlinedTextField
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontFamily
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.input.PasswordVisualTransformation
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import com.speedsoftware.nibbler.ui.AppState
import com.speedsoftware.nibbler.ui.Screen
import com.speedsoftware.nibbler.ui.theme.NibblerColors

@Composable
fun LoginScreen(appState: AppState) {
    val scope = rememberCoroutineScope()
    val flow = remember { LoginFlow(appState, scope) }

    // On the web the form is a real HTML <form>, so browser password managers can save and fill it
    if (isNativeLoginFormAvailable()) {
        Box(modifier = Modifier.fillMaxSize().background(NibblerColors.DarkBackground))
        NativeLoginForm(
            flow = flow,
            onInvite = { token -> appState.navigateTo(Screen.AcceptInvite(token)) },
            onForgotPassword = { appState.navigateTo(Screen.ResetPassword()) }
        )
        return
    }

    var email by remember { mutableStateOf("") }
    var password by remember { mutableStateOf("") }
    var code by remember { mutableStateOf("") }
    val errorMessage = flow.errorMessage
    val isLoading = flow.isLoading

    Box(
        modifier = Modifier
            .fillMaxSize()
            .background(NibblerColors.DarkBackground)
            .padding(16.dp),
        contentAlignment = Alignment.Center
    ) {
        Box(
            modifier = Modifier
                .widthIn(max = 440.dp)
                .fillMaxWidth()
                .background(NibblerColors.DarkSurfaceContainer)
                .border(1.dp, NibblerColors.DarkOutlineThin, RoundedCornerShape(8.dp))
        ) {
            Column(
                modifier = Modifier.padding(28.dp),
                horizontalAlignment = Alignment.CenterHorizontally,
                verticalArrangement = Arrangement.Center
            ) {
                // Bauhaus Logo Block
                Row(verticalAlignment = Alignment.CenterVertically) {
                    Box(
                        modifier = Modifier
                            .size(32.dp)
                            .background(NibblerColors.BauhausRed),
                        contentAlignment = Alignment.Center
                    ) {
                        Text("N", fontFamily = FontFamily.SansSerif, fontWeight = FontWeight.Bold, color = Color.White, fontSize = 18.sp)
                    }
                    Spacer(modifier = Modifier.width(12.dp))
                    Column {
                        Text(
                            text = "Nibbler",
                            fontFamily = FontFamily.SansSerif,
                            fontWeight = FontWeight.Bold,
                            fontSize = 24.sp,
                            color = NibblerColors.BauhausCoral
                        )
                        Text(
                            text = "Sign in to continue",
                            fontFamily = FontFamily.SansSerif,
                            fontSize = 9.sp,
                            fontWeight = FontWeight.Bold,
                            color = NibblerColors.DarkOnSurfaceVariant,
                            letterSpacing = 0.08.sp
                        )
                    }
                }

                Spacer(modifier = Modifier.height(20.dp))
                Box(modifier = Modifier.fillMaxWidth().height(1.dp).background(NibblerColors.DarkOutlineThin))
                Spacer(modifier = Modifier.height(20.dp))

                if (flow.step == LoginStep.CREDENTIALS) {
                    OutlinedTextField(
                        value = email,
                        onValueChange = { email = it; flow.errorMessage = null },
                        label = { Text("Email", fontFamily = FontFamily.SansSerif, fontSize = 11.sp) },
                        singleLine = true,
                        shape = RoundedCornerShape(8.dp),
                        modifier = Modifier.fillMaxWidth()
                    )

                    Spacer(modifier = Modifier.height(16.dp))

                    OutlinedTextField(
                        value = password,
                        onValueChange = { password = it; flow.errorMessage = null },
                        label = { Text("Password", fontFamily = FontFamily.SansSerif, fontSize = 11.sp) },
                        visualTransformation = PasswordVisualTransformation(),
                        singleLine = true,
                        shape = RoundedCornerShape(8.dp),
                        modifier = Modifier.fillMaxWidth()
                    )

                    Spacer(modifier = Modifier.height(16.dp))

                    OutlinedTextField(
                        value = flow.serverUrl,
                        onValueChange = { flow.updateServerUrl(it) },
                        label = { Text("Server URL", fontFamily = FontFamily.SansSerif, fontSize = 11.sp) },
                        singleLine = true,
                        shape = RoundedCornerShape(8.dp),
                        modifier = Modifier.fillMaxWidth()
                    )
                } else {
                    Text(
                        text = "Enter the 6-digit code from your authenticator app, or one of your recovery codes.",
                        color = NibblerColors.DarkOnSurfaceVariant,
                        fontSize = 13.sp,
                        modifier = Modifier.fillMaxWidth()
                    )
                    Spacer(modifier = Modifier.height(16.dp))
                    OutlinedTextField(
                        value = code,
                        onValueChange = { code = it; flow.errorMessage = null },
                        label = { Text("Authentication code", fontFamily = FontFamily.SansSerif, fontSize = 11.sp) },
                        singleLine = true,
                        shape = RoundedCornerShape(8.dp),
                        modifier = Modifier.fillMaxWidth()
                    )
                }

                if (flow.step == LoginStep.CREDENTIALS) {
                    Spacer(modifier = Modifier.height(4.dp))
                    Box(modifier = Modifier.fillMaxWidth(), contentAlignment = Alignment.CenterEnd) {
                        androidx.compose.material3.TextButton(
                            onClick = { appState.navigateTo(Screen.ResetPassword()) }
                        ) {
                            Text(
                                "Forgot password?",
                                fontFamily = FontFamily.SansSerif,
                                fontSize = 11.sp,
                                color = NibblerColors.Accent
                            )
                        }
                    }
                }

                if (errorMessage != null) {
                    Spacer(modifier = Modifier.height(12.dp))
                    Text(
                        text = errorMessage,
                        color = NibblerColors.Error,
                        fontFamily = FontFamily.SansSerif,
                        fontSize = 11.sp
                    )
                }

                Spacer(modifier = Modifier.height(24.dp))

                Button(
                    onClick = {
                        if (flow.step == LoginStep.CREDENTIALS) flow.submitCredentials(email, password)
                        else flow.submitCode(code)
                    },
                    shape = RoundedCornerShape(8.dp),
                    colors = ButtonDefaults.buttonColors(
                        containerColor = NibblerColors.BauhausCoral,
                        contentColor = Color.White
                    ),
                    enabled = !isLoading,
                    modifier = Modifier.fillMaxWidth().height(48.dp)
                ) {
                    if (isLoading) {
                        CircularProgressIndicator(
                            color = Color.White,
                            strokeWidth = 2.dp,
                            modifier = Modifier.size(20.dp)
                        )
                    } else {
                        Text(
                            if (flow.step == LoginStep.CREDENTIALS) "Log in" else "Verify",
                            fontFamily = FontFamily.SansSerif,
                            fontWeight = FontWeight.Bold,
                            fontSize = 13.sp,
                            letterSpacing = 0.05.sp
                        )
                    }
                }

                if (flow.step == LoginStep.TWO_FACTOR) {
                    androidx.compose.material3.TextButton(
                        onClick = { code = ""; flow.backToCredentials() },
                        modifier = Modifier.fillMaxWidth()
                    ) {
                        Text("Back", fontFamily = FontFamily.SansSerif, fontSize = 11.sp, color = NibblerColors.DarkOnSurfaceVariant)
                    }
                }

                Spacer(modifier = Modifier.height(16.dp))
                Box(modifier = Modifier.fillMaxWidth().height(1.dp).background(NibblerColors.DarkOutlineThin))
                Spacer(modifier = Modifier.height(12.dp))

                var showTokenInput by remember { mutableStateOf(false) }
                var inviteTokenInput by remember { mutableStateOf("") }

                if (!showTokenInput) {
                    androidx.compose.material3.TextButton(
                        onClick = { showTokenInput = true },
                        modifier = Modifier.fillMaxWidth()
                    ) {
                        Text(
                            "Have an invite token?",
                            fontFamily = FontFamily.SansSerif,
                            fontSize = 11.sp,
                            fontWeight = FontWeight.Bold,
                            color = NibblerColors.BauhausYellow
                        )
                    }
                } else {
                    Column(modifier = Modifier.fillMaxWidth(), verticalArrangement = Arrangement.spacedBy(10.dp)) {
                        OutlinedTextField(
                            value = inviteTokenInput,
                            onValueChange = { inviteTokenInput = it },
                            label = { Text("Invite token", fontFamily = FontFamily.SansSerif, fontSize = 11.sp) },
                            singleLine = true,
                            shape = RoundedCornerShape(8.dp),
                            modifier = Modifier.fillMaxWidth()
                        )
                        Row(modifier = Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.spacedBy(8.dp)) {
                            androidx.compose.material3.OutlinedButton(
                                onClick = { showTokenInput = false },
                                shape = RoundedCornerShape(8.dp),
                                modifier = Modifier.weight(1f)
                            ) {
                                Text("Cancel", fontFamily = FontFamily.SansSerif, fontSize = 11.sp)
                            }
                            Button(
                                onClick = {
                                    if (inviteTokenInput.isNotBlank()) {
                                        appState.navigateTo(Screen.AcceptInvite(inviteTokenInput.trim()))
                                    }
                                },
                                shape = RoundedCornerShape(8.dp),
                                colors = ButtonDefaults.buttonColors(containerColor = NibblerColors.Accent, contentColor = Color.White),
                                modifier = Modifier.weight(1f)
                            ) {
                                Text("Join", fontFamily = FontFamily.SansSerif, fontWeight = FontWeight.Bold, fontSize = 11.sp)
                            }
                        }
                    }
                }
            }
        }
    }
}

@androidx.compose.ui.tooling.preview.Preview
@Composable
fun LoginScreenPreview() {
    LoginScreen(appState = AppState())
}
